import React, {ErrorInfo, FC, ReactNode, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {ErrorBoundaryFallback} from './ErrorBoundaryFallback';
import {reportToCoralogix} from '../../app/rum';

export const ErrorBoundary: FC<IErrorBoundaryProps> = ({children}) => {
	const location = useLocation();
	const [error, setError] = useState<string>('');

	useEffect(() => {
		window.addEventListener('error', handleError);
		return () => {
			window.removeEventListener('error', handleError);
		};
	}, []);

	useEffect(() => {
		setError('');
	}, [location.pathname]);

	const handleError = (event: ErrorEvent) => {
		reportToCoralogix(event.error, {
			source: 'window.error',
			filename: event?.filename,
			lineno: event?.lineno,
			colno: event?.colno,
		});

		setError(event?.message || 'An unexpected error occurred');
		console.error(event);
		return false;
	};

	if (error)
		return <ErrorBoundaryFallback msg={error}/>;

	return (
		<ErrorBoundaryReact
			fallback={({message}) => {
				setTimeout(() => setError(message), 1);
			}}>
			{children}
		</ErrorBoundaryReact>
	);
};


class ErrorBoundaryReact extends React.Component<IErrorBoundaryReactProps, IErrorBoundaryReactState> {
	state: IErrorBoundaryReactState = {hasError: false};

	static getDerivedStateFromError(error: Error): IErrorBoundaryReactState {
		return {hasError: true, error};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('ErrorBoundary caught:', error, errorInfo.componentStack);
		reportToCoralogix(error, {
			source: 'react.errorBoundary',
			componentStack: errorInfo.componentStack,
		});
	}

	render() {
		if (this.state.hasError) {
			this.props.fallback(this.state.error);
			return null;
		}

		return this.props.children;
	}
}


interface IErrorBoundaryProps {
	children: ReactNode;
}

interface IErrorBoundaryReactProps {
	children: ReactNode;
	fallback?: (error: Error) => ReactNode | void;
}

interface IErrorBoundaryReactState {
	hasError: boolean;
	error?: Error;
}
